import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OberonDialog from '~components/OberonDialog';
import { getContactFlows } from '~pages/SipConfiguration/api';
import { ContactFlow, TrunkFlow, UpsertTrunkFlow } from '~pages/SipConfiguration/domain';

type Props = {
  open: boolean;
  submitting: boolean;
  trunk: TrunkFlow | null;
  onAccept: (data: UpsertTrunkFlow) => void;
  onClose: () => void;
};

type Form = {
  trunk: string;
  contactFlow: ContactFlow | null;
};

type Page = {
  dataLoaded: boolean;
  contactFlows: ContactFlow[];
  dataLoadError: {
    contactFlows: string;
  };
};

const TrunkFlowModal = ({ open, submitting, trunk, onAccept, onClose }: Props) => {
  const isLoading = submitting;
  const isEdit = Boolean(trunk);
  const [page, setPage] = useState<Page>({
    dataLoaded: false,
    contactFlows: [],
    dataLoadError: {
      contactFlows: '',
    },
  });
  const {
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    control,
  } = useForm<Form>({
    defaultValues: {
      trunk: '',
      contactFlow: null,
    },
    mode: 'all',
    shouldUnregister: true,
  });

  // Data load event
  useEffect(() => {
    const load = async () => {
      try {
        const flows = await getContactFlows();
        setPage((prev) => ({ ...prev, contactFlows: flows }));
      } catch (e) {
        setPage((prev) => ({
          ...prev,
          dataLoadError: { ...prev.dataLoadError, contactFlows: 'Unable to fetch contact flow list.' },
        }));
      } finally {
        setPage((prev) => ({ ...prev, dataLoaded: true }));
      }
    };

    load();

    return () => {
      setPage({
        dataLoaded: false,
        contactFlows: [],
        dataLoadError: {
          contactFlows: '',
        },
      });
    };
  }, [open]);

  // Form prefill construction event
  useEffect(() => {
    // If edit lets prefill all required fields
    if (page.dataLoaded && trunk !== null) {
      setValue('trunk', trunk.trunk);
      setValue('contactFlow', page.contactFlows.find((item) => item.contactFlowId === trunk.contactFlowId) || null);
    }

    // Reset form on close
    return () => {
      reset();
    };
  }, [trunk, page.dataLoaded]);

  const onSubmit = handleSubmit(async (data: Form) => {
    const body: UpsertTrunkFlow = {
      trunk: data.trunk,
      contactFlowId: data.contactFlow!.contactFlowId,
    };
    try {
      await onAccept(body);

      reset();
    } catch (e) {
      // Do nothing, catch error to prevent form reset on failed action
    }
  });

  return (
    <OberonDialog
      open={open}
      onSubmit={onSubmit}
      onClose={onClose}
      title={`${isEdit ? 'Edit' : 'Create'} Trunk Flow`}
      subHeader={
        <Typography variant='caption' component='h1' color='textSecondary'>
          Create a linkage between a trunk and a contact flow.
        </Typography>
      }
      content={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='trunk'
              control={control}
              rules={{
                required: 'trunk name is required.',
                maxLength: 60,
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Trunk Name'
                  disabled={submitting || isEdit}
                  required={true}
                  error={Boolean(errors.trunk)}
                  helperText={errors.trunk?.message || `Must be globally unique, max length is 60.`}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='contactFlow'
              control={control}
              rules={{ required: 'Contact flow is required' }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  onChange={(e, data) => {
                    field.onChange(data);
                  }}
                  fullWidth
                  options={page.contactFlows}
                  filterSelectedOptions
                  disabled={isLoading}
                  isOptionEqualToValue={(option, value) => option.contactFlowId === value.contactFlowId}
                  getOptionLabel={(option) => option?.name || ''}
                  renderOption={(props, option, state) => (
                    <li {...props} key={`${option.contactFlowId}-${option.name}`}>
                      <div>
                        <Typography variant='body2' color='textPrimary' component='p'>
                          {option.name}
                        </Typography>
                      </div>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Contact Flow'
                      required={true}
                      error={Boolean(page.dataLoadError.contactFlows || errors.contactFlow)}
                      helperText={page.dataLoadError.contactFlows || errors.contactFlow?.message}
                      variant='outlined'
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
      }
      actionFooter={
        <>
          <Button variant='text' disabled={isLoading} onClick={onClose}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={isLoading}
            loading={isLoading}>
            {isEdit ? 'Update' : 'Create'}
          </LoadingButton>
        </>
      }
    />
  );
};

export default TrunkFlowModal;
